import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { Adapt } from './lib/adapt.js';
import { SceneClock } from './lib/scene-clock.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { getHersheyFont } from '../hershey-font.js';
import { vdjColor, vdjBeatChroma, vdjCameraKick, VdjPost, VdjComfort, VDJ_PRESETS, VDJ_DROP_FLASH } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Un quad plein écran et deux quads d'onomatopée.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'comic',
  family: 'shader',
  danceability: 0.88,
  energyLevel: 0.82,
  bpmMin: 90,
  bpmMax: 175,
  movement: 0.80,
  tonality: 0.50,
  enabled: true
};

// Comic : une planche de bande dessinée pop-art qui remplit l'écran, 3 à 6
// cases aux gouttières blanches encrées d'un trait noir épais ; dans les
// cases, la pochette encrée et tramée, des lignes de vitesse, des
// explosions, des gros plans. Sur la mesure la planche se RECOMPOSE ; sur les
// kicks une onomatopée géante éclate par-dessus. Rock, pop, hip-hop, funk,
// électro, punk. Règles R1-R19 de kiosk-app/CLAUDE.md, preset party de
// lib/vdjLook.js.
//
// L'ENCRE : un filtre calculé UNE FOIS par pochette dans une cible 1024²
// (tier 0 : 512²) : R la luminance posterisée à 4 tons, G la luminance, B la
// force des contours (Sobel) - cette dernière, réduite à 16 x 16 et lue une
// fois, donne la SAILLANCE : les gros plans visent les trois cellules les
// plus dessinées. Tout ce qui est NET se décide à l'affichage, en pixels
// d'écran (R1) : le trait d'encre est la différence de ton entre quatre
// lectures à ±1,5 px (une ligne de ~3 px quelle que soit l'échelle de la
// case), la trame Ben-Day est une grille tournée de 15° en pixels d'écran
// dont le rayon des points suit le ton, bord fondu sur 1 px.
// Couleurs d'impression limitées : cyan, magenta, jaune, rouge, noir, sur un
// papier crème au grain fin ; la palette tourne à chaque phrase.
//
// LES CASES : un découpage parmi dix (3 à 6 cases ; 8 à 9 en montée ; une
// pleine page au drop), les rectangles et l'état de chaque case dans une
// texture flottante 18 x 3 (un tableau d'uniformes n'est garanti qu'à 16
// vecteurs) : la planche PRÉCÉDENTE reste dessous pendant que la nouvelle
// glisse en place, pour qu'aucune zone ne reste vide. Types : PLAN LARGE (toute la pochette, une par planche, R20),
// GROS PLAN (zoom x3 sur une cellule saillante), ACTION (lignes de vitesse
// radiales qui défilent, la pochette au centre), EXPLOSION (bulle à pointes,
// aplats jaune et rouge), TEXTE (cartouche au titre, fonte manuscrite
// Hershey script, domaine public), SPECTRE (barres encrées). Cadres et
// gouttières en pixels, épaisseur qui varie le long du bord (dessinés à la
// main). Sur la mesure les cases glissent en place depuis la droite en
// 1/3 de temps, décalées l'une après l'autre (tourner de page).
//
// LES ONOMATOPÉES : neuf mots originaux (BOOM, POW, BAM, ZAP, WHAM, KRAK,
// VROOM, BLAM, ZOOM) plus le premier mot du titre, rendus une fois dans un
// atlas (Inter 700, OFL, de public/fonts, sinon Helvetica) : masque de
// remplissage en R, contour en G. Sur un kick fort (bande grave > 0,6 ou
// attaque), au plus une par temps : échelle 160 % -> 100 % en 1/8 de temps,
// tenue un temps, dissipée ; dégradé jaune -> rouge ou aplat de palette,
// contour noir, ombre décalée, légère perspective.
//
// LE RYTHME (party, R9) : TEMPS onomatopée possible, une case tremble ;
// MESURE nouveau découpage ; PHRASE palette d'impression, densité de trame ;
// MONTÉE plus de cases, plus petites, lignes de vitesse partout ; DROP une
// seule case pleine page, la pochette en explosion, onomatopée géante,
// flash (VdjComfort). CONFORT (R10) : le même shader en 32 x 32 toutes les
// 0,5 s, gain par VdjComfort nourri de mesures fraîches.
//
// Debug : ?comiclayout=<n> fige le découpage ; ?comicsfx=1 une onomatopée à
// chaque temps ; ?comicink=1 montre le filtre encre.

const INK_RES = { 0: 512, 1: 1024, 2: 1024 };
const DOT_CELL = { 0: 10, 1: 7, 2: 6 };           // trame, px d'écran
const MAXP = 18;                   // 9 cases de la planche courante + 9 de la précédente, qui reste dessous pendant le tourner de page
const SCENE_S = 32;
const GUTTER_PX = 10;
const WORDS = ['BOOM', 'POW', 'BAM', 'ZAP', 'WHAM', 'KRAK', 'VROOM', 'BLAM', 'ZOOM'];
const T = { WIDE: 0, CLOSE: 1, ACTION: 2, EXPLO: 3, TEXT: 4, SPECTRUM: 5, DROP: 6 };
// les découpages : rectangles (x, y, w, h) en uv de page (y vers le haut)
const LAYOUTS = [
  [[0, 0.5, 1, 0.5], [0, 0, 0.5, 0.5], [0.5, 0, 0.5, 0.5]],
  [[0, 0.5, 0.5, 0.5], [0.5, 0.5, 0.5, 0.5], [0, 0, 0.5, 0.5], [0.5, 0, 0.5, 0.5]],
  [[0, 0.5, 0.333, 0.5], [0.333, 0.5, 0.334, 0.5], [0.667, 0.5, 0.333, 0.5], [0, 0, 0.5, 0.5], [0.5, 0, 0.5, 0.5]],
  [[0, 0.5, 0.333, 0.5], [0.333, 0.5, 0.334, 0.5], [0.667, 0.5, 0.333, 0.5], [0, 0, 0.333, 0.5], [0.333, 0, 0.334, 0.5], [0.667, 0, 0.333, 0.5]],
  [[0, 0, 0.42, 1], [0.42, 0.667, 0.58, 0.333], [0.42, 0.333, 0.58, 0.334], [0.42, 0, 0.58, 0.333]],
  [[0, 0, 0.6, 1], [0.6, 0.5, 0.4, 0.5], [0.6, 0, 0.4, 0.5]],
  [[0, 0, 0.35, 1], [0.35, 0.45, 0.65, 0.55], [0.35, 0, 0.325, 0.45], [0.675, 0, 0.325, 0.45]],
  [[0, 0.667, 0.333, 0.333], [0.333, 0.667, 0.334, 0.333], [0.667, 0.667, 0.333, 0.333], [0, 0.333, 0.333, 0.334], [0.333, 0.333, 0.334, 0.334], [0.667, 0.333, 0.333, 0.334], [0, 0, 0.333, 0.333], [0.333, 0, 0.334, 0.333], [0.667, 0, 0.333, 0.333]],
  [[0, 0.5, 0.25, 0.5], [0.25, 0.5, 0.25, 0.5], [0.5, 0.5, 0.25, 0.5], [0.75, 0.5, 0.25, 0.5], [0, 0, 0.25, 0.5], [0.25, 0, 0.25, 0.5], [0.5, 0, 0.25, 0.5], [0.75, 0, 0.25, 0.5]],
  [[0, 0, 1, 1]]
];
const NORMAL_LAYOUTS = [0, 1, 2, 3, 4, 5, 6], BUILD_LAYOUTS = [7, 8, 3], DROP_LAYOUT = 9;
// les palettes d'impression : (A, B) deux encres de couleur, en linéaire
const PRINTS = [
  [[0.0, 0.55, 0.85], [0.85, 0.05, 0.4]],        // cyan, magenta
  [[0.9, 0.75, 0.02], [0.8, 0.04, 0.03]],        // jaune, rouge
  [[0.85, 0.05, 0.4], [0.9, 0.75, 0.02]],        // magenta, jaune
  [[0.0, 0.55, 0.85], [0.9, 0.75, 0.02]]         // cyan, jaune
];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function hash1(n) { const x = Math.sin(n * 12.9898) * 43758.5453; return x - Math.floor(x); }

export function firstWord(title) {
  const w = (title || '').toUpperCase().replace(/[^A-Z0-9 ]/g, ' ').trim().split(/\s+/)[0] || '';
  return w.length >= 2 && w.length <= 8 ? w : '';
}

// l'atlas des onomatopées : 10 mots, 2 colonnes x 5 lignes de 1024 x 204 ;
// R le remplissage, G le contour (coloriés dans le shader)
function wordAtlas(words, fontFamily) {
  if (typeof document === 'undefined') return null;
  const W = 2048, H = 1024, CW = 1024, CH = 204;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d');
  if (!g) return null;
  g.fillStyle = '#000'; g.fillRect(0, 0, W, H);
  g.textAlign = 'center'; g.textBaseline = 'middle'; g.lineJoin = 'round';
  words.forEach((w, i) => {
    const x0 = (i % 2) * CW, y0 = Math.floor(i / 2) * CH;
    let size = 180;
    g.font = `900 ${size}px ${fontFamily}`;
    while (size > 60 && g.measureText(w).width > CW - 120) { size -= 10; g.font = `900 ${size}px ${fontFamily}`; }
    const cx = x0 + CW / 2, cy = y0 + CH / 2 + size * 0.04;
    // le contour épais (G) puis le remplissage (R)
    g.lineWidth = size * 0.16; g.strokeStyle = '#00ff00'; g.strokeText(w, cx, cy);
    g.fillStyle = '#ff0000'; g.fillText(w, cx, cy);
  });
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.NoColorSpace;
  t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter;
  t.wrapS = THREE.ClampToEdgeWrapping; t.wrapT = THREE.ClampToEdgeWrapping;
  t.generateMipmaps = false;
  t.needsUpdate = true;
  return t;
}

// le cartouche : le titre en Hershey script (polylignes, trait de 6 px), R l'encre
function captionTexture(text) {
  if (typeof document === 'undefined') return null;
  const font = getHersheyFont();
  const W = 1024, H = 256;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d');
  if (!g) return null;
  g.fillStyle = '#000'; g.fillRect(0, 0, W, H);
  g.strokeStyle = '#fff'; g.lineCap = 'round'; g.lineJoin = 'round';
  const t = (text || '').slice(0, 24);
  if (font && font.glyphs) {
    let adv = 0;
    for (const ch of t) adv += font.advances[ch] != null ? font.advances[ch] : 0.6;
    const scale = Math.min(150, (W - 80) / Math.max(adv, 1));
    g.lineWidth = Math.max(4, scale * 0.05);
    let x = (W - adv * scale) / 2;
    const base = H * 0.62;
    for (const ch of t) {
      const gl = font.glyphs[ch];
      if (gl) for (const s of gl) {
        g.beginPath();
        s.forEach(([px, py], k) => { const X = x + px * scale, Y = base - py * scale; if (k === 0) g.moveTo(X, Y); else g.lineTo(X, Y); });
        g.stroke();
      }
      x += (font.advances[ch] != null ? font.advances[ch] : 0.6) * scale;
    }
  } else {
    g.fillStyle = '#fff'; g.font = 'italic 700 120px "Helvetica Neue", Helvetica, Arial, sans-serif'; g.textAlign = 'center'; g.textBaseline = 'middle';
    g.fillText(t, W / 2, H / 2);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.NoColorSpace;
  tex.minFilter = THREE.LinearFilter; tex.magFilter = THREE.LinearFilter;
  tex.wrapS = THREE.ClampToEdgeWrapping; tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

const QUAD_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// --- le filtre encre : une fois par pochette --------------------------------------
const INK_FRAGMENT = `
  precision highp float;
  uniform sampler2D uCover;
  uniform float uHasCover;
  uniform vec2 uRes;
  varying vec2 vUv;
  float lumAt(vec2 uv) {
    if (uHasCover < 0.5) {
      // sans pochette : un motif pop (disques concentriques et rayons)
      vec2 p = uv - 0.5; float r = length(p), a = atan(p.y, p.x);
      return 0.3 + 0.35 * step(0.5, fract(r * 7.0)) + 0.3 * step(0.5, fract(a * 2.0 / 3.1416));
    }
    vec3 c = texture2D(uCover, uv).rgb;
    return dot(c, vec3(0.2126, 0.7152, 0.0722));
  }
  void main() {
    vec2 px = 1.0 / uRes;
    // la luminance un peu lissée, puis quatre tons
    float l = 0.0;
    for (int j = -1; j <= 1; j++) for (int i = -1; i <= 1; i++) l += lumAt(vUv + vec2(float(i), float(j)) * px * 1.5);
    l /= 9.0;
    float lg = pow(l, 0.8);
    float tone = floor(lg * 3.999) / 3.0;
    // Sobel sur la luminance lissée : la force des contours (saillance)
    float tl = lumAt(vUv + vec2(-1.0, 1.0) * px * 2.0), tc = lumAt(vUv + vec2(0.0, 1.0) * px * 2.0), tr = lumAt(vUv + vec2(1.0, 1.0) * px * 2.0);
    float ml = lumAt(vUv + vec2(-1.0, 0.0) * px * 2.0), mr = lumAt(vUv + vec2(1.0, 0.0) * px * 2.0);
    float bl = lumAt(vUv + vec2(-1.0, -1.0) * px * 2.0), bc = lumAt(vUv + vec2(0.0, -1.0) * px * 2.0), br = lumAt(vUv + vec2(1.0, -1.0) * px * 2.0);
    float gx = (tr + 2.0 * mr + br) - (tl + 2.0 * ml + bl), gy = (tl + 2.0 * tc + tr) - (bl + 2.0 * bc + br);
    float edge = clamp(length(vec2(gx, gy)) * 1.5, 0.0, 1.0);
    gl_FragColor = vec4(tone, lg, edge, 1.0);
  }
`;

// --- la planche -------------------------------------------------------------------
const PAGE_FRAGMENT = `
  precision highp float;
  uniform sampler2D uInk;
  uniform sampler2D uPanels;     // 9 x 3 flottant : rect | type, graine, cx, cy | décalage x, y, secousse, -
  uniform sampler2D uCaption;
  uniform float uN;              // cases
  uniform vec2 uRes;
  uniform float uTime;
  uniform float uBeat;
  uniform vec3 uInkA;
  uniform vec3 uInkB;
  uniform vec3 uPaper;
  uniform float uDotCell;        // px
  uniform float uPxScale;        // 1 à l'écran ; 32/largeur dans la passe de mesure 32 x 32 (tout ce qui est en pixels garde sa part de l'image)
  uniform float uDotScale;       // densité de trame (phrase)
  uniform vec3 uBands;
  uniform float uCoverAspect;
  uniform float uShowInk;
  uniform float uGain;
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  const vec3 INK = vec3(0.012, 0.010, 0.012);
  const vec3 YELLOW = vec3(0.95, 0.78, 0.03), RED = vec3(0.82, 0.05, 0.03);
  float h21(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float vnoise(vec2 p) {
    vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
    return mix(mix(h21(i), h21(i + vec2(1.0, 0.0)), f.x), mix(h21(i + vec2(0.0, 1.0)), h21(i + vec2(1.0, 1.0)), f.x), f.y);
  }
  vec4 panel(float i, float row) { return texture2D(uPanels, vec2((i + 0.5) / ${MAXP}.0, (row + 0.5) / 3.0)); }
  // la pochette encrée en uv : le ton et le trait (différence de ton à ±1,5 px d'écran)
  // pxUv : la taille d'un pixel d'écran en uv de pochette
  vec3 inked(vec2 uv, vec2 pxUv, vec3 inkA, vec3 inkB) {
    vec3 t0 = texture2D(uInk, uv).rgb;
    float tone = t0.r;
    float dmax = 0.0;
    // quatre lectures à ±2,2 px d'écran : un trait d'encre de ~4,5 px, à toute échelle
    vec2 tap = pxUv * 2.2 * uPxScale;
    dmax = max(dmax, abs(texture2D(uInk, uv + vec2(tap.x, 0.0)).r - tone));
    dmax = max(dmax, abs(texture2D(uInk, uv - vec2(tap.x, 0.0)).r - tone));
    dmax = max(dmax, abs(texture2D(uInk, uv + vec2(0.0, tap.y)).r - tone));
    dmax = max(dmax, abs(texture2D(uInk, uv - vec2(0.0, tap.y)).r - tone));
    float line = smoothstep(0.12, 0.3, dmax);
    // les aplats et la trame : noir, points de couleur sur papier, papier
    vec2 sp = gl_FragCoord.xy;
    float cs = 0.9659, sn = 0.2588;
    float cellPx = uDotCell * uPxScale;
    vec2 rp = vec2(sp.x * cs - sp.y * sn, sp.x * sn + sp.y * cs) / cellPx;
    vec2 cell = floor(rp), f = fract(rp) - 0.5;
    float d = length(f) * cellPx;
    float cover = 0.0;
    vec3 col;
    if (tone < 0.2) { col = INK; }
    else if (tone < 0.5) { cover = 0.78 * uDotScale; col = inkB; }
    else if (tone < 0.84) { cover = 0.42 * uDotScale; col = inkA; }
    else { cover = 0.0; col = uPaper; }
    float r = cellPx * 0.56 * sqrt(cover);
    float dot = 1.0 - smoothstep(r - 0.7 * uPxScale, r + 0.7 * uPxScale, d);
    vec3 c = tone < 0.2 ? INK : mix(uPaper, col, dot * step(0.001, cover));
    return mix(c, INK, line);
  }
  // les lignes de vitesse radiales : convergentes, qui défilent
  float speedLines(vec2 p, float seed, float density) {
    float a = atan(p.y, p.x), r = length(p);
    float k = a / 6.2832 * density;
    float w = 0.12 + 0.3 * h21(vec2(floor(k), seed));
    float len = 0.25 + 0.75 * h21(vec2(floor(k), seed + 7.0));
    float on = step(fract(k), w) * step(1.0 - len, fract(r * 1.3 - uBeat * 0.9 + h21(vec2(floor(k), seed + 3.0))));
    return on * step(0.18, r);
  }
  float starburst(vec2 p, float n, float seed) {
    float a = atan(p.y, p.x), r = length(p);
    float spike = abs(fract(a * n / 6.2832 + seed) * 2.0 - 1.0);
    float re = 0.30 + 0.13 * spike + 0.02 * sin(a * 3.0 + seed * 6.0);
    return r - re;                                             // < 0 dedans
  }
  void main() {
    vec2 sp = gl_FragCoord.xy;
    vec3 col = vec3(0.86, 0.85, 0.8);                          // la gouttière, blanc cassé
    float gpx = ${GUTTER_PX.toFixed(1)} * uPxScale;
    float inPanel = 0.0;
    for (int k = 0; k < ${MAXP}; k++) {
      float fi = float(k);
      if (fi >= uN) break;
      vec4 R = panel(fi, 0.0), I = panel(fi, 1.0), O = panel(fi, 2.0);
      vec2 p0 = (R.xy + O.xy) * uRes + vec2(gpx * 0.5), p1 = (R.xy + R.zw + O.xy) * uRes - vec2(gpx * 0.5);
      p0 += O.z * vec2(h21(vec2(fi, floor(uTime * 60.0))) - 0.5, h21(vec2(fi + 9.0, floor(uTime * 60.0))) - 0.5) * 8.0 * uPxScale;
      p1 += O.z * vec2(h21(vec2(fi, floor(uTime * 60.0))) - 0.5, h21(vec2(fi + 9.0, floor(uTime * 60.0))) - 0.5) * 8.0 * uPxScale;
      vec2 q = (sp - p0) / (p1 - p0);                           // 0..1 dans la case
      if (q.x < 0.0 || q.x > 1.0 || q.y < 0.0 || q.y > 1.0) continue;
      inPanel = 1.0;
      vec2 sizePx = p1 - p0;
      float type = I.x, seed = I.y;
      vec2 pc = (q - 0.5) * vec2(sizePx.x / sizePx.y, 1.0);    // repère centré, y = ±0.5
      vec3 c = uPaper;
      if (type < 0.5 || type > 5.5) {
        // PLAN LARGE (et la pleine page du drop) : la pochette couvre la case, zoom lent
        float z = 1.0 + 0.06 * fract(uBeat * 0.0625 + seed) + (type > 5.5 ? 0.15 : 0.0);
        vec2 cuv = pc / z * vec2(1.0, uCoverAspect) / max(sizePx.x / sizePx.y, 1.0) * 1.0 + 0.5;
        float side = max(sizePx.x / sizePx.y, 1.0) * z;
        c = inked(cuv, vec2(1.0 / sizePx.y / side), uInkA, uInkB);
        if (type > 5.5) {
          // le drop : la pochette dans une bulle d'explosion pleine page
          float sb = starburst(pc * 0.7, 14.0, seed);
          float sbpx = sb * sizePx.y;
          vec3 ex = mix(YELLOW, RED, smoothstep(-0.25, 0.05, sb) * 0.0 + smoothstep(-0.1, 0.0, sb));
          c = mix(c, ex, step(0.0, sbpx) * (1.0 - step(26.0, sbpx)));
          c = mix(c, INK, 1.0 - smoothstep(0.0, 2.5, abs(sbpx)));
          c = mix(c, INK, 1.0 - smoothstep(0.0, 2.5, abs(sbpx - 26.0)));
        }
      } else if (type < 1.5) {
        // GROS PLAN : zoom x3 sur une cellule saillante, dérive lente
        vec2 centre = I.zw + 0.03 * vec2(sin(uBeat * 0.3 + seed * 6.0), cos(uBeat * 0.23 + seed * 4.0));
        float z = 3.0 + 0.3 * sin(uBeat * 0.4 + seed);
        vec2 cuv = pc / z * vec2(1.0, uCoverAspect) + centre;
        c = inked(cuv, vec2(1.0 / sizePx.y / z), uInkA, uInkB);
      } else if (type < 2.5) {
        // ACTION : lignes de vitesse vers le centre, la pochette au centre
        float sl = speedLines(pc, seed, 56.0);
        c = mix(uPaper, INK, sl);
        float r = length(pc);
        float rr = 0.24;
        if (r < rr) {
          vec2 cuv = pc / (rr * 2.0) * vec2(1.0, uCoverAspect) + 0.5;
          c = inked(cuv, vec2(1.0 / sizePx.y / (rr * 2.0)), uInkA, uInkB);
        }
        float ring = abs(r - rr) * sizePx.y;
        c = mix(c, INK, 1.0 - smoothstep(2.0, 4.0, ring));
      } else if (type < 3.5) {
        // EXPLOSION : bulle à pointes, jaune et rouge, lignes derrière
        float sl = speedLines(pc, seed + 2.0, 40.0) * 0.6;
        c = mix(uPaper, INK, sl);
        float sb = starburst(pc, 12.0, seed);
        float sbpx = sb * sizePx.y;
        float sb2 = starburst(pc * 1.6, 12.0, seed);
        vec3 ex = mix(YELLOW, RED, smoothstep(0.0, 0.06, -sb2) * 0.0 + step(0.0, -sb2) * 0.0);
        ex = sb2 < 0.0 ? RED : YELLOW;
        c = mix(c, ex, step(sbpx, 0.0));
        c = mix(c, INK, 1.0 - smoothstep(0.0, 3.0, abs(sbpx)));
        c = mix(c, INK, (1.0 - smoothstep(0.0, 2.0, abs(sb2 * sizePx.y))) * step(sbpx, 0.0));
      } else if (type < 4.5) {
        // TEXTE : trame légère, cartouche crème au titre
        float cs = 0.9659, sn = 0.2588;
        vec2 rp = vec2(sp.x * cs - sp.y * sn, sp.x * sn + sp.y * cs) / uDotCell;
        float d = length(fract(rp) - 0.5) * uDotCell;
        c = mix(uPaper, uInkA, 1.0 - smoothstep(1.6, 2.6, d));
        vec2 box0 = vec2(0.08, 0.3), box1 = vec2(0.92, 0.7);
        vec2 bd = max(box0 - q, q - box1);
        float bpx = max(bd.x * sizePx.x, bd.y * sizePx.y);
        if (bpx < 0.0) {
          vec2 tuv = (q - box0) / (box1 - box0);
          float ink = texture2D(uCaption, vec2(tuv.x, tuv.y)).r;
          c = mix(vec3(0.80, 0.76, 0.64), INK, ink);
        }
        c = mix(c, INK, 1.0 - smoothstep(0.0, 3.0, abs(bpx)));
      } else {
        // SPECTRE : douze barres encrées
        float k = floor(q.x * 12.0), f = fract(q.x * 12.0);
        float t = k / 11.0;
        float lvl = 0.12 + 0.8 * clamp(mix(uBands.x, uBands.z, t) * (0.7 + 0.5 * h21(vec2(k, floor(uBeat * 2.0)))), 0.0, 1.0);
        float bar = step(0.12, f) * step(f, 0.88) * step(q.y, lvl);
        vec2 ed = vec2(min(f - 0.12, 0.88 - f) * sizePx.x / 12.0, (lvl - q.y) * sizePx.y);
        float epx = min(ed.x, ed.y);
        c = mix(uPaper, mix(uInkA, uInkB, step(0.5, lvl)), bar);
        c = mix(c, INK, bar * (1.0 - smoothstep(1.5, 3.5, epx)));
      }
      // le cadre dessiné à la main : 4 à 6 px, l'épaisseur varie le long du bord
      vec2 ed = min(sp - p0, p1 - sp);
      float dpx = min(ed.x, ed.y);
      float along = ed.x < ed.y ? sp.y : sp.x;
      float th = (4.0 + 2.0 * vnoise(vec2(along * 0.02 / uPxScale, seed * 10.0))) * uPxScale;
      c = mix(c, INK, 1.0 - smoothstep(th - 0.8 * uPxScale, th + 0.8 * uPxScale, dpx));
      col = c;
    }
    // le grain du papier
    col *= 0.97 + 0.06 * (h21(floor(sp / 1.5)) - 0.5);
    if (uShowInk > 0.5) { vec3 t = texture2D(uInk, vUv).rgb; col = vec3(t.r); }
    col *= uGain;
    vec2 sc = vec2((vUv.x * 2.0 - 1.0) * uAspect, vUv.y * 2.0 - 1.0);
    vec2 qs = abs(sc - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(qs, 0.0)) + min(max(qs.x, qs.y), 0.0);
    col *= mix(0.45, 1.0, smoothstep(0.0, uSafeFeather, sd));
    gl_FragColor = vec4(col, 1.0);
  }
`;

// --- l'onomatopée : un quad en espace écran ----------------------------------------
const SFX_VERTEX = `
  precision highp float;
  uniform vec4 uSfx;             // centre x, y (uv écran), échelle, alpha
  uniform vec2 uRes;
  uniform float uSkew;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    vec2 size = vec2(0.56, 0.56 * 204.0 / 1024.0) * uSfx.z;
    vec2 p = (uv - 0.5) * size;
    p.x += p.y * uSkew;                                        // la perspective
    p.y *= uRes.x / uRes.y;
    gl_Position = vec4((uSfx.xy + p) * 2.0 - 1.0, 0.0, 1.0);
  }
`;
const SFX_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uWords;
  uniform highp vec4 uSfx;        // même précision qu'au vertex
  uniform float uWord;           // 0..9
  uniform vec3 uColA;
  uniform vec3 uColB;
  varying vec2 vUv;
  vec2 tile(vec2 uv) { return vec2((mod(uWord, 2.0) + uv.x) / 2.0, 1.0 - (floor(uWord / 2.0) + 1.0 - uv.y) / 5.0); }
  void main() {
    vec2 sh = vec2(0.022, -0.028);                              // l'ombre décalée
    vec4 m = texture2D(uWords, tile(vUv));
    vec4 ms = texture2D(uWords, tile(vUv - sh));
    float fill = m.r, outline = max(m.g, m.r), shadow = max(ms.g, ms.r);
    vec3 col = mix(uColA, uColB, vUv.y);
    vec3 c = mix(vec3(0.012), col, fill);
    float a = max(outline, shadow * 0.85) * uSfx.w;
    c = mix(vec3(0.012), c, outline);
    gl_FragColor = vec4(c * a, a);
  }
`;

export class ComicVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    const fl = parseInt(urlParam('comiclayout'), 10);
    this._forceLayout = fl >= 0 && fl < LAYOUTS.length ? fl : null;
    this._forceSfx = urlParam('comicsfx') === '1';
    this._showInk = urlParam('comicink') === '1';
    this._post = new VdjPost({ bloomStrength: 0.15, bloomThreshold: 0.9, grain: 0.0, vignette: 0.15, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort(VDJ_PRESETS.party.comfort);
  }

  get postProcessing() { return this._post.value; }

  init(scene) {
    this._scene = scene;
    this._adapt = new Adapt();
    this._clock = new SceneClock({ period: SCENE_S, start: 0 });
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 1);
    this._words = WORDS.slice(); this._titleWord = '';
    this._wordsTex = null; this._fontFamily = '"Helvetica Neue", Helvetica, Arial, sans-serif';
    this._captionTex = captionTexture('HALOFLOOR') || new THREE.DataTexture(new Uint8Array([0, 0, 0, 255]), 1, 1, THREE.RGBAFormat);
    this._captionTex.needsUpdate = true;
    this._caption = ''; this._coverUrl = undefined; this._coverTex = null; this._coverAspect = 1;
    // la police des onomatopées : Inter 700 (OFL) depuis public/fonts, sinon Helvetica
    const self = this;
    this._buildWords();
    if (typeof FontFace !== 'undefined' && typeof document !== 'undefined') {
      try {
        const ff = new FontFace('ComicInter', 'url(fonts/inter-700.woff2)', { weight: '700' });
        ff.load().then((f) => { document.fonts.add(f); self._fontFamily = 'ComicInter, "Helvetica Neue", Helvetica, Arial, sans-serif'; self._buildWords(); }).catch(() => {});
      } catch (e) { /* Helvetica */ }
    }
    // les cases : 9 x 3 flottant
    this._pdata = new Float32Array(MAXP * 3 * 4);
    this._ptex = new THREE.DataTexture(this._pdata, MAXP, 3, THREE.RGBAFormat, THREE.FloatType);
    this._ptex.minFilter = this._ptex.magFilter = THREE.NearestFilter; this._ptex.generateMipmaps = false; this._ptex.needsUpdate = true;
    this._inkA = new THREE.Color(0, 0.55, 0.85); this._inkB = new THREE.Color(0.85, 0.05, 0.4); this._paper = new THREE.Color(0.62, 0.56, 0.43);   // papier crème sombre (journal) : le gain de confort reste près de 1

    const res = INK_RES[this._tier] || 1024;
    this._inkU = { uCover: { value: null }, uHasCover: { value: 0 }, uRes: { value: new THREE.Vector2(res, res) } };
    this._inkScene = new THREE.Scene();
    const im = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._inkU, vertexShader: QUAD_VERTEX, fragmentShader: INK_FRAGMENT, depthTest: false, depthWrite: false }));
    im.frustumCulled = false; this._inkScene.add(im); this._inkMesh = im;
    this._inkRT = new THREE.WebGLRenderTarget(res, res, { type: THREE.UnsignedByteType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, wrapS: THREE.MirroredRepeatWrapping, wrapT: THREE.MirroredRepeatWrapping, generateMipmaps: false });
    this._salRT = new THREE.WebGLRenderTarget(16, 16, { type: THREE.UnsignedByteType, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, depthBuffer: false });
    this._salBuf = new Uint8Array(16 * 16 * 4);
    this._blitU = { uTex: { value: this._inkRT.texture } };
    this._blitScene = new THREE.Scene();
    const bm = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._blitU, vertexShader: QUAD_VERTEX, fragmentShader: 'precision mediump float; uniform sampler2D uTex; varying vec2 vUv; void main() { gl_FragColor = texture2D(uTex, vUv); }', depthTest: false, depthWrite: false }));
    bm.frustumCulled = false; this._blitScene.add(bm); this._blitMesh = bm;
    this._salient = [[0.5, 0.5], [0.3, 0.6], [0.7, 0.4]];

    this._u = {
      uInk: { value: this._inkRT.texture }, uPanels: { value: this._ptex }, uCaption: { value: this._captionTex }, uN: { value: 3 },
      uRes: { value: new THREE.Vector2(1920, 1080) }, uTime: { value: 0 }, uBeat: { value: 0 },
      uInkA: { value: this._inkA }, uInkB: { value: this._inkB }, uPaper: { value: this._paper },
      uDotCell: { value: DOT_CELL[this._tier] || 7 }, uPxScale: { value: 1 }, uDotScale: { value: 1 }, uBands: { value: new THREE.Vector3(0.3, 0.3, 0.3) },
      uCoverAspect: { value: 1 }, uShowInk: { value: this._showInk ? 1 : 0 }, uGain: { value: 1 },
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._quad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._u, vertexShader: QUAD_VERTEX, fragmentShader: PAGE_FRAGMENT, depthTest: false, depthWrite: false }));
    this._quad.frustumCulled = false; this._quad.renderOrder = -3000;
    this._quad.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._quad);
    // deux onomatopées au plus en vie
    this._sfx = [];
    for (let i = 0; i < 2; i++) {
      const u = { uSfx: { value: new THREE.Vector4(0.5, 0.5, 1, 0) }, uRes: this._u.uRes, uSkew: { value: 0 }, uWords: { value: null }, uWord: { value: 0 }, uColA: { value: new THREE.Color(0.95, 0.78, 0.03) }, uColB: { value: new THREE.Color(0.82, 0.05, 0.03) } };
      const m = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: u, vertexShader: SFX_VERTEX, fragmentShader: SFX_FRAGMENT, transparent: true, depthTest: false, depthWrite: false, blending: THREE.CustomBlending, blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor }));
      m.frustumCulled = false; m.renderOrder = -2990; m.visible = false;
      scene.add(m);
      this._sfx.push({ mesh: m, u, t0: -9, life: 0 });
    }
    this._measScene = new THREE.Scene();
    const mq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._quad.material);
    mq.frustumCulled = false; this._measScene.add(mq);
    this._measRT = new THREE.WebGLRenderTarget(32, 32, { type: THREE.UnsignedByteType, depthBuffer: false, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
    this._measBuf = new Uint8Array(32 * 32 * 4);

    this._time = 0; this._beats = 0; this._bars = 0; this._beatAge = 9; this._barAge = 9; this._lastSfxBeat = -9;
    this._build = 0; this._dropAge = 99; this._phraseN = 0; this._paletteRef = undefined; this._inkDirty = true; this._salDirty = false;
    this._lumAge = 0; this._lumMeasured = 0.3; this._lumFresh = false; this._gain = 1; this._paused = false; this._dirtyLayout = true;
    this._panels = []; this._layoutIdx = 0; this._shakeIdx = -1; this._shakeAge = 9; this._beatS = 0.5;
    this._newLayout(0, false, false);
  }

  _buildWords() {
    const words = this._words.slice(0, 9);
    words.push(this._titleWord || 'HALO');
    const t = wordAtlas(words, this._fontFamily);
    if (!t) return;
    if (this._wordsTex) this._wordsTex.dispose();
    this._wordsTex = t;
    for (const s of this._sfx || []) s.u.uWords.value = t;
  }

  _applyPalette(pal) {
    const pr = PRINTS[this._phraseN % PRINTS.length];
    this._inkA.setRGB(pr[0][0], pr[0][1], pr[0][2]);
    this._inkB.setRGB(pr[1][0], pr[1][1], pr[1][2]);
    // la pochette donne une des deux encres une phrase sur deux
    if (pal && pal.vividColors && pal.vividColors.length && this._phraseN % 2 === 1) vdjColor(pal.vividColors[0], this._inkA, VDJ_PRESETS.party.saturation);
  }

  _loadCover(profile) {
    if (profile.artworkUrl === this._coverUrl) return;
    this._coverUrl = profile.artworkUrl;
    if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; }
    this._inkU.uHasCover.value = 0; this._inkDirty = true;
    if (!profile.artworkUrl) return;
    const self = this, url = profile.artworkUrl;
    loadArtworkTexture(url, { wrap: THREE.MirroredRepeatWrapping, mipmaps: true }, (tex) => {
      if (!self._scene || url !== self._coverUrl) { tex.dispose(); return; }
      self._coverTex = tex; self._inkU.uCover.value = tex; self._inkU.uHasCover.value = 1;
      const img = tex.image;
      self._coverAspect = img && img.width ? img.width / img.height : 1;
      self._u.uCoverAspect.value = self._coverAspect;
      self._inkDirty = true;
    }, () => {});
  }

  onTrackChange(profile) {
    this._loadCover(profile);
    const w = firstWord(profile && profile.title);
    if (w !== this._titleWord) { this._titleWord = w; this._buildWords(); }
    const cap = ((profile && profile.title) || '').slice(0, 24) || 'HALOFLOOR';
    if (cap !== this._caption) {
      this._caption = cap;
      const t = captionTexture(cap);
      if (t) { if (this._captionTex) this._captionTex.dispose(); this._captionTex = t; this._u.uCaption.value = t; }
    }
  }

  // un nouveau découpage : les types (une PLAN LARGE par planche, R20), les
  // cibles des gros plans, et le glissement d'entrée depuis la droite
  _newLayout(bar, build, drop) {
    let li;
    if (this._forceLayout != null) li = this._forceLayout;
    else if (drop) li = DROP_LAYOUT;
    else if (build) li = BUILD_LAYOUTS[bar % BUILD_LAYOUTS.length];
    else li = NORMAL_LAYOUTS[Math.floor(hash1(bar * 3.3 + 0.7) * NORMAL_LAYOUTS.length)];
    const L = LAYOUTS[li];
    this._layoutIdx = li;
    const wide = Math.floor(hash1(bar * 5.1 + 0.2) * L.length);
    this._panels = L.map((r, i) => {
      let type;
      if (drop) type = T.DROP;
      else if (i === wide) type = T.WIDE;
      else {
        const h = hash1(bar * 7.7 + i * 1.3 + 0.5);
        if (build) type = h < 0.6 ? T.ACTION : h < 0.8 ? T.EXPLO : T.CLOSE;
        else type = h < 0.3 ? T.CLOSE : h < 0.52 ? T.ACTION : h < 0.7 ? T.EXPLO : h < 0.85 ? T.TEXT : T.SPECTRUM;
      }
      const sal = this._salient[i % this._salient.length];
      return { r, type, seed: hash1(bar * 2.1 + i * 9.7 + 0.3), cx: sal[0], cy: sal[1], delay: i * 0.05, from: 1.15 - r[0] };
    });
    this._prev = (this._prev === undefined ? [] : this._panels0) || [];
    this._panels0 = this._panels;
    this._layoutT0 = this._time;
    this._u.uN.value = this._prev.length + this._panels.length;
  }

  _writePanels(now) {
    const d = this._pdata;
    const dur = this._beatS / 3;
    // la planche précédente d'abord (figée, sans glissement), la nouvelle par-dessus
    const prev = this._prev || [];
    prev.forEach((p, i) => {
      d.set([p.r[0], p.r[1], p.r[2], p.r[3]], i * 4);
      d.set([p.type, p.seed, p.cx, p.cy], (MAXP + i) * 4);
      d.set([0, 0, 0, 0], (2 * MAXP + i) * 4);
    });
    const base = prev.length;
    this._panels.forEach((p, j) => {
      const i = base + j;
      const k = clamp01((now - this._layoutT0 - p.delay * this._beatS) / dur);
      const e = 1 - Math.pow(1 - k, 3);
      const ox = p.from * (1 - e);
      const shake = j === this._shakeIdx ? Math.max(0, 1 - this._shakeAge / 0.25) : 0;
      d.set([p.r[0], p.r[1], p.r[2], p.r[3]], i * 4);
      d.set([p.type, p.seed, p.cx, p.cy], (MAXP + i) * 4);
      d.set([ox, 0, shake, 0], (2 * MAXP + i) * 4);
    });
    this._ptex.needsUpdate = true;
  }

  _fire(now, word, big, beatS) {
    const s = this._sfx[this._sfxNext = ((this._sfxNext || 0) + 1) % 2];
    s.t0 = now; s.life = beatS * 1.3; s.big = big;
    s.u.uWord.value = word;
    s.u.uSkew.value = (hash1(now * 13.1) - 0.5) * 0.5;
    s.x = 0.3 + 0.4 * hash1(now * 3.7); s.y = 0.35 + 0.3 * hash1(now * 5.3);
    const pr = PRINTS[(this._phraseN + 1) % PRINTS.length];
    if (hash1(now * 7.9) < 0.5) { s.u.uColA.value.setRGB(0.95, 0.78, 0.03); s.u.uColB.value.setRGB(0.82, 0.05, 0.03); }
    else { s.u.uColA.value.setRGB(pr[0][0], pr[0][1], pr[0][2]); s.u.uColB.value.setRGB(pr[1][0], pr[1][1], pr[1][2]); }
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    this._paused = paused;
    const dt = paused ? 0 : Math.min(deltaTime, 0.05);
    const a = this._adapt.update(dt, profile);
    if (profile.artworkPalette !== this._paletteRef) { this._paletteRef = profile.artworkPalette; this._applyPalette(profile.artworkPalette); }
    if (profile.artworkUrl !== this._coverUrl) this._loadCover(profile);
    if (this._caption === '' && profile.title) this.onTrackChange(profile);
    if (paused) return;                                  // l'image tient
    this._time += dt;
    this._clock.update(dt);
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 124;
    const beatS = this._beatS = 60 / bpm, barS = 4 * beatS;
    const now = this._time;
    const director = profile.director || null;
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.6);
    this._build += (build - this._build) * (1 - Math.exp(-dt / 1.0));
    const low = a.bands ? a.bands.low : a.pulse;

    if (a.beat) {
      this._beats++; this._beatAge = 0;
      // une case tremble
      this._shakeIdx = Math.floor(hash1(this._beats * 1.7) * this._panels.length); this._shakeAge = 0;
      // l'onomatopée : kick fort, au plus une par temps, jamais en avalanche
      const kick = low > 0.6 || a.attack > 0.5;
      if ((kick && a.intensity > 0.45 && this._beats - this._lastSfxBeat >= 1 && hash1(this._beats * 4.1) < 0.7) || this._forceSfx) {
        this._lastSfxBeat = this._beats;
        const useTitle = this._titleWord && hash1(this._beats * 2.3) < 0.3;
        this._fire(now, useTitle ? 9 : Math.floor(hash1(this._beats * 6.7) * 9), false, beatS);
      }
    }
    this._beatAge += dt; this._shakeAge += dt;
    if (a.bar) {
      this._bars++; this._barAge = 0;
      if (this._dropAge > barS) this._newLayout(this._bars, this._build > 0.4, false);
    }
    this._barAge += dt;
    if (a.phrase) { this._phraseN++; this._applyPalette(profile.artworkPalette); this._u.uDotScale.value = 0.8 + 0.4 * hash1(this._phraseN * 3.1); }
    if (this._clock.wrapped && this._dropAge > barS) this._newLayout(this._bars + 100, false, false);
    if (a.drop) {
      this._dropAge = 0;
      this._newLayout(this._bars, false, true);
      this._fire(now, Math.floor(hash1(now) * 9), true, beatS);
      this._sfx[this._sfxNext].life = beatS * 2.5;
      if (director && this._comfort.requestFlash(this._time)) director.flash = VDJ_DROP_FLASH;
    }
    this._dropAge += dt;
    this._writePanels(now);

    const bp = a.beatPhase != null ? a.beatPhase : clamp01(this._beatAge / beatS);
    const kick = vdjCameraKick(a.barPhase != null ? a.barPhase : clamp01(this._barAge / barS), this._bars, 4);
    for (const s of this._sfx) {
      const age = now - s.t0;
      if (age < 0 || age > s.life) { s.mesh.visible = false; continue; }
      s.mesh.visible = true;
      const inT = beatS / 8;
      const scale = age < inT ? 1.6 - 0.6 * (age / inT) : 1.0;
      const fade = 1 - clamp01((age - beatS) / Math.max(s.life - beatS, 0.05));
      s.u.uSfx.value.set(s.x, s.y, (s.big ? 1.7 : 1.0) * scale * kick.zoom, fade);
    }
    const u = this._u;
    u.uTime.value = now; u.uBeat.value = this._beats + bp;
    const b = a.bands || { low: 0.4 + 0.4 * a.pulse, mid: 0.4 * a.intensity, high: 0.3 * a.intensity };
    u.uBands.value.set(clamp01(b.low), clamp01(b.mid), clamp01(b.high));
    this._lumAge += dt;
    if (this._lumFresh) { this._lumFresh = false; this._gain = this._comfort.measure(this._lumMeasured, 0.5); }
    u.uGain.value = this._gain;
    this._post.update(vdjBeatChroma(bp, beatS));
  }

  _render(renderer) {
    // la taille de la CIBLE courante (la passe de rendu du composer), pas du
    // canvas : gl_FragCoord vit dans la cible, et au tier 0 elle est plus
    // petite - avec la taille du canvas la planche ne remplissait que le
    // coin bas-gauche
    const cur = renderer.getRenderTarget();
    if (cur) _size.set(cur.width, cur.height); else renderer.getDrawingBufferSize(_size);
    const w = Math.max(2, _size.x), h = Math.max(2, _size.y);
    const prev = renderer.getRenderTarget();
    renderer.getClearColor(_cc); const ca = renderer.getClearAlpha();
    const autoClear = renderer.autoClear;
    renderer.autoClear = false;
    if (this._inkDirty) {
      // le filtre encre, une fois par pochette, puis la saillance (16 x 16 lue une fois)
      this._inkDirty = false;
      renderer.setRenderTarget(this._inkRT);
      renderer.render(this._inkScene, this._ortho);
      renderer.setRenderTarget(this._salRT);
      renderer.render(this._blitScene, this._ortho);
      try {
        renderer.readRenderTargetPixels(this._salRT, 0, 0, 16, 16, this._salBuf);
        const cells = [];
        for (let j = 2; j < 14; j++) for (let i = 2; i < 14; i++) cells.push({ e: this._salBuf[(j * 16 + i) * 4 + 2], x: (i + 0.5) / 16, y: (j + 0.5) / 16 });
        cells.sort((p, q) => q.e - p.e);
        const pick = [];
        for (const c of cells) { if (pick.every((p) => Math.hypot(p[0] - c.x, p[1] - c.y) > 0.25)) pick.push([c.x, c.y]); if (pick.length >= 3) break; }
        if (pick.length) { this._salient = pick; for (const p of this._panels) { const s = pick[Math.floor(hash1(p.seed * 31) * pick.length)]; p.cx = s[0]; p.cy = s[1]; } }
      } catch (e) { /* la saillance attendra la prochaine pochette */ }
    }
    if (this._lumAge >= 0.5 && !this._paused) {
      this._lumAge = 0;
      this._u.uRes.value.set(32, 32); this._u.uPxScale.value = 32 / w;
      renderer.setRenderTarget(this._measRT);
      renderer.setClearColor(0x000000, 1);
      renderer.clear(true, false, false);
      renderer.render(this._measScene, this._ortho);
      try {
        renderer.readRenderTargetPixels(this._measRT, 0, 0, 32, 32, this._measBuf);
        let sum = 0;
        for (let i = 0; i < this._measBuf.length; i += 4) sum += 0.2126 * this._measBuf[i] + 0.7152 * this._measBuf[i + 1] + 0.0722 * this._measBuf[i + 2];
        this._lumMeasured = sum / (32 * 32 * 255);
        this._lumFresh = true;
      } catch (e) { /* la prochaine fois */ }
      renderer.setClearColor(_cc, ca);
    }
    this._u.uRes.value.set(w, h); this._u.uPxScale.value = 1;
    renderer.setRenderTarget(prev);
    renderer.autoClear = autoClear;
  }

  getProbe() {
    const live = this._sfx.filter((s) => s.mesh.visible).length;
    return { t: +this._time.toFixed(2), beats: this._beats, bars: this._bars, layout: this._layoutIdx, panels: this._panels.length, types: this._panels.map((p) => p.type).join(''), sfx: live, build: +this._build.toFixed(3), lum: +this._lumMeasured.toFixed(3), gain: +this._gain.toFixed(3), cover: this._inkU.uHasCover.value, salient: this._salient.map((p) => p.map((v) => +v.toFixed(2))), word: this._titleWord, font: this._fontFamily.split(',')[0] };
  }

  dispose() {
    if (this._quad) { this._quad.onBeforeRender = null; if (this._scene) this._scene.remove(this._quad); this._quad.geometry.dispose(); this._quad.material.dispose(); }
    for (const s of this._sfx || []) { if (this._scene) this._scene.remove(s.mesh); s.mesh.geometry.dispose(); s.mesh.material.dispose(); }
    for (const m of [this._inkMesh, this._blitMesh]) if (m) { m.geometry.dispose(); m.material.dispose(); }
    for (const t of [this._inkRT, this._salRT, this._measRT]) if (t) t.dispose();
    for (const t of [this._ptex, this._wordsTex, this._captionTex, this._coverTex]) if (t) t.dispose();
    if (this._adapt) this._adapt.dispose();
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
const _cc = new THREE.Color();
